import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import type { OrderDeliveryDto } from '@/ApiClient';
import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { entityCache, keyedLruCache, pruneIds } from '@/store/cache-primitives';

export interface OrderTableCache {
  filterKey: string;
  orderedIds: number[];
}

// Table caches are keyed per filter combination so switching filters back and forth
// restores instantly.
const MAX_TABLE_CACHES = 20;

export const useOrderDeliveries = defineStore('order-delivery-cache', () => {
  const entities = entityCache<OrderDeliveryDto>();
  const deliveries = entities.data;
  const lru = keyedLruCache<OrderTableCache>(MAX_TABLE_CACHES);
  const tableCaches = lru.data;

  // Snapshot persistence contract — see store/warm-boot.ts. The orders page's
  // fetchOrders() always refetches after seeding from these caches, so disk-seeded
  // (possibly stale) table caches revalidate on page mount without extra machinery.
  const snapshotDto = 'OrderDeliveryDto' as const;
  // OrderTableCache is a frontend-defined shape the DTO hash can't guard — bump
  // this whenever the snapshot/OrderTableCache shape changes.
  const SNAPSHOT_VERSION = 1;

  type OrderDeliveriesSnapshot = {
    version: number;
    deliveries: Record<number, OrderDeliveryDto>;
    tableCaches: Record<string, OrderTableCache>;
  };

  function snapshotData(): OrderDeliveriesSnapshot {
    // The orders page mutates cached objects in place with frontend-only fields:
    // dragCount and addFiles (real File objects). Strip them so File blobs never
    // reach IndexedDB. toRaw: structured clone can't handle Vue's reactive proxies.
    const cleaned: Record<number, OrderDeliveryDto> = {};
    for (const [id, delivery] of Object.entries(toRaw(deliveries.value))) {
      const { dragCount, addFiles, ...rest } = toRaw(delivery) as OrderDeliveryDto & {
        dragCount?: number;
        addFiles?: File[];
      };
      cleaned[Number(id)] = rest;
    }
    return { version: SNAPSHOT_VERSION, deliveries: cleaned, tableCaches: toRaw(tableCaches.value) };
  }

  function seedSnapshot(data: unknown) {
    const snapshot = data as Partial<OrderDeliveriesSnapshot>;
    if (snapshot.version !== SNAPSHOT_VERSION) return;
    if (Object.keys(deliveries.value).length > 0 || Object.keys(tableCaches.value).length > 0) return;
    entities.seed(snapshot.deliveries ?? {});
    tableCaches.value = snapshot.tableCaches ?? {};
  }

  function getOrdered(filterKey: string): OrderDeliveryDto[] {
    const cache = lru.get(filterKey);
    if (!cache) return [];
    return cache.orderedIds.map((id) => deliveries.value[id]).filter(Boolean);
  }

  function setTableState(filterKey: string, ids: number[]) {
    lru.set(filterKey, { filterKey, orderedIds: ids });
  }

  function remove(ids: number[]) {
    entities.remove(ids);
    pruneIds(tableCaches, ids);
  }

  function clear() {
    entities.clear();
    lru.clear();
  }

  onTabResume((elapsed) => {
    // Drop only the list caches so the next page mount fetches a fresh list;
    // keep the delivery DTOs so anything rendered shows data instead of skeletons.
    if (elapsed >= CACHE_TTL) lru.clear();
  });

  return {
    deliveries,
    tableCaches,
    snapshotDto,
    snapshotData,
    seedSnapshot,
    get: entities.get,
    getTableCache: lru.get,
    getOrdered,
    cache: entities.cache,
    setTableState,
    update: entities.set,
    remove,
    clear,
  };
});
